import { useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { Transaction } from '../types/Transaction';
import { buildQueryParams } from '../utils/buildQueryParams';
import { withForcedLedgerReadMode } from '../utils/ledgerReadMode';
import { authFetch } from '../lib/authFetch';

export interface CalendarDailyEntry {
  date: string;
  committedNetDelta: number;
  tentativeNetDelta: number;
  runningCommittedBalance: number;
  runningProjectedBalance: number;
}

export interface CalendarResponse {
  range: {
    startDate: string;
    endDate: string;
  };
  events: Transaction[];
  daily: CalendarDailyEntry[];
}

export const useCalendar = (
  range: { startDate: string; endDate: string } | null,
  includeTentative: boolean = true,
  refreshKey: number = 0
) => {
  const { auth } = useAuth();
  const [data, setData] = useState<CalendarResponse | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!range?.startDate || !range?.endDate) return;

    let cancelled = false;
    const fetchCalendar = async () => {
      setLoading(true);
      try {
        const params = buildQueryParams({
          startDate: range.startDate,
          endDate: range.endDate,
          includeTentative: includeTentative ? 'true' : 'false',
        });

        const res = await authFetch(withForcedLedgerReadMode(`/api/transactions/calendar?${params}`), {
          headers: { Authorization: `Bearer ${auth.token}` },
        });

        if (!res.ok) {
          const errData = await res.json();
          throw new Error(errData.message || 'Failed to load calendar');
        }

        const payload = (await res.json()) as CalendarResponse;
        if (!cancelled) setData(payload);
      } catch (err) {
        console.error('Calendar fetch error:', err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchCalendar();
    return () => {
      cancelled = true;
    };
  }, [auth.token, range?.startDate, range?.endDate, includeTentative, refreshKey]);

  return { data, loading };
};
